import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid,
} from '@mui/material';
import { editarCustasDadosJuridicos } from '../../../../services/core/processos/processoDadosJuridicosService';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível alterar o lançamento. Tente novamente.';

function dataIso(dataLancamento) {
  return dataLancamento ? new Date(dataLancamento).toISOString().split('T')[0] : '';
}

function makeFormFieldsIniciais(content) {
  return {
    data: dataIso(content?.dataLancamento),
    historico: content?.descricao ?? '',
    valor: content?.valor ?? '',
  };
}

/**
 * Port de `DialogEditarCustas.jsx` real (`DadosJuridicos/TabelaCustas/`) para
 * MUI v5 — AW2-1108 Etapa 4. Mesmos 3 campos de `DialogNovoCustas.jsx`,
 * pré-preenchidos a partir de `content` (a linha clicada) — reidratados a
 * cada abertura via `aoAbrir` de `useSessaoDialogMutacao` (nunca via
 * `useEffect([content])` puro: `content` já é montado a partir do CLIQUE no
 * momento certo pelo componente pai, então reidratar só na transição real
 * fechado->aberto evita reescrever o formulário em edição se `content`
 * mudar de referência por outro motivo, ex. refetch da grid).
 *
 * Mesmo padrão de `DialogNovoCustas.jsx`: sem Snackbar próprio (fecha
 * imediatamente em sucesso E erro, mesmo comportamento real) — `onSucesso`/
 * `onErro` sobem para o componente pai.
 */
export default function DialogEditarCustas({
  open, content, idProcesso, onClose, onSucesso, onErro,
}) {
  const [formFields, setFormFields] = useState(() => makeFormFieldsIniciais(content));

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setFormFields(makeFormFieldsIniciais(content));
  });

  const onChangeCampo = nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
  };

  const disableConfirmar = !formFields.data || !formFields.historico || !formFields.valor;

  const onSubmit = async () => {
    if (enviandoRef.current || disableConfirmar || !content) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;

    try {
      await editarCustasDadosJuridicos(
        toBrazilianDate(formFields.data),
        formFields.historico,
        Number(formFields.valor),
        content.idProcessoCustas,
        idProcesso,
      );
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onClose();
      onSucesso();
    } catch {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onClose();
      onErro(MENSAGEM_ERRO_GENERICA);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alteração no Valor de Custas do Processo</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              fullWidth
              label="Data*"
              value={formFields.data}
              onChange={onChangeCampo('data')}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Histórico*"
              value={formFields.historico}
              onChange={onChangeCampo('historico')}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              type="number"
              fullWidth
              label="Valor*"
              value={formFields.valor}
              onChange={onChangeCampo('valor')}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={onSubmit} variant="contained" disabled={disableConfirmar}>Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

DialogEditarCustas.propTypes = {
  open: PropTypes.bool,
  content: PropTypes.shape({
    idProcessoCustas: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dataLancamento: PropTypes.string,
    descricao: PropTypes.string,
    valor: PropTypes.number,
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
  onErro: PropTypes.func.isRequired,
};

DialogEditarCustas.defaultProps = {
  open: false,
  content: null,
  idProcesso: null,
};
